import classNames from "classnames";
import { Form, useFormikContext } from "formik";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Picker } from "@design-system/Picker/Picker.lib";
import { GridCol } from "@design-system/Grid/GridCol";
import { GridRow } from "@design-system/Grid/GridRow";
import { EnterKeyHint } from "@components/onboarding/components/EnterKeyHint";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { EntityProfileType } from "@common/types/entity";
import { FC } from "react";
import animation from "../layout/OnboardingLayout.module.scss";
import { entityTypeItems, isEntityProfileType } from "../constants";
import type { CreateOrganizationFormSchema } from "../organizationOnboardingForms";
import { Footer } from "../components/Footer";
import { WorkspaceTimezoneField } from "../components/WorkspaceTimezoneField";
import fieldSpacing from "../components/fieldSpacing.module.scss";
import styles from "./Profile.module.scss";
import commonStyles from "./common.module.scss";

const fields = createFormikFields<CreateOrganizationFormSchema>();

interface Props {
    formBusy: boolean;
    onBack: () => void;
}

export const OrganizationClientProfile: FC<Props> = ({ formBusy, onBack }) => {
    const { isValid } = useFormikContext();

    return (
        <div className={commonStyles.content420}>
            <header className={animation.fadeIn2}>
                <h1 className={classNames(commonStyles.title, styles.header)}>
                    Set up your firm's workspace
                    <br /> for free
                </h1>
            </header>

            <Form className={animation.fadeIn1}>
                <fields.Text
                    name="businessName"
                    label="Workspace name"
                    size="xl"
                    className={fieldSpacing.inputField}
                />

                <GridRow>
                    <GridCol sm={6}>
                        <FormikField<EntityProfileType>
                            name="legalType"
                            label="Entity type"
                            control="button"
                            className={fieldSpacing.buttonField}
                        >
                            {({ helpers, field, invalid }) => (
                                <Picker.Select
                                    items={entityTypeItems}
                                    value={
                                        field.value &&
                                        isEntityProfileType(field.value)
                                            ? field.value
                                            : null
                                    }
                                    onValueChange={(value) => {
                                        if (value) {
                                            helpers.setValue(value);
                                        }
                                    }}
                                    placeholder="Select"
                                    aria-invalid={invalid}
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="md"
                                            block
                                        />
                                    }
                                />
                            )}
                        </FormikField>
                    </GridCol>

                    <GridCol sm={6}>
                        <fields.Date
                            name="bookkeepingStartDate"
                            label="Bookkeeping start date"
                            labelHidden
                            className={fieldSpacing.buttonField}
                            variant="panel-input"
                            pickerLabel="Bookkeeping start date"
                            placeholder="Select"
                            buttonProps={{
                                variant: "primary",
                                size: "xl",
                                block: true,
                            }}
                        />
                    </GridCol>
                </GridRow>

                <WorkspaceTimezoneField name="timezone" />

                <Footer onBack={onBack} onBackDisabled={formBusy}>
                    <ButtonWithLoader
                        loading={formBusy}
                        variant="primary"
                        disabled={!isValid}
                        type="submit"
                        size="xl"
                        rightIcon={<EnterKeyHint />}
                    >
                        Submit
                    </ButtonWithLoader>
                </Footer>
            </Form>
        </div>
    );
};
